import assign from 'object-assign';

import GenericStore from './GenericStore.js';

const scales = [
  {
    name: 'C Major',
    sharps: []
  }, {
    name: 'G Major',
    sharps: [1] // means sharp on the top line
  }, {
    name: 'D Major',
    sharps: [1, 4]
  }, {
    name: 'A Major',
    sharps: [1, 4, 0]
  }, {
    name: 'E Major',
    sharps: [1, 4, 0, 3]
  }, {
    name: 'B Major',
    sharps: [1, 4, 0, 3, 6]
  }, {
    name: 'F♯ Major',
    sharps: [1, 4, 0, 3, 6, 2]
  }, {
    name: 'C♯ Major',
    sharps: [1, 4, 0, 3, 6, 2, 5]
  }, {
    name: 'C♭ Major',
    flats: [5, 2, 6, 3, 7, 4, 8]
  }, {
    name: 'G♭ Major',
    flats: [5, 2, 6, 3, 7, 4]
  }, {
    name: 'D♭ Major',
    flats: [5, 2, 6, 3, 7]
  }, {
    name: 'A♭ Major',
    flats: [5, 2, 6, 3]
  }, {
    name: 'E♭ Major',
    flats: [5, 2, 6]
  }, {
    name: 'B♭ Major',
    flats: [5, 2]
  }, {
    name: 'F Major',
    flats: [5]
  }
];

const notes = [
  {
    note: 'G',
    line: -5,
    image: 'g1.png',
    position: '0'
  }, {
    note: 'A',
    line: -4,
    image: 'a2.png',
    position: '1'
  }, {
    note: 'B',
    line: -3,
    image: 'b2.png',
    position: '↑2'
  }, {
    note: 'C',
    line: -2,
    image: 'c2.png',
    position: '3'
  }, {
    note: 'D',
    line: -1,
    image: 'd2.png',
    position: '0'
  }, {
    note: 'E',
    line: 0,
    image: 'e2.png',
    position: '1'
  }, {
    note: 'F',
    line: 1,
    image: 'f2.png',
    position: '↓2'
  }, {
    note: 'G',
    line: 2,
    image: 'g2.png',
    position: '3'
  }, {
    note: 'A',
    line: 3,
    image: 'a3.png',
    position: '0'
  }, {
    note: 'B',
    line: 4,
    image: 'b3.png',
    position: '1'
  }, {
    note: 'C',
    line: 5,
    image: 'c3.png',
    position: '↓2'
  }, {
    note: 'D',
    line: 6,
    image: 'd3.png',
    position: '3'
  }, {
    note: 'E',
    line: 7,
    image: 'e3.png',
    position: '0'
  }, {
    note: 'F',
    line: 8,
    image: 'f3.png',
    position: '↓1'
  }, {
    note: 'G',
    line: 9,
    image: 'g3.png',
    position: '↓2'
  }, {
    note: 'A',
    line: 10,
    image: 'a4.png',
    position: '3'
  }, {
    note: 'B',
    line: 11,
    image: 'b4.png',
    position: '4'
  }, {
    note: 'C',
    line: 12,
    image: 'c4.png',
    position: '3³'
  }, {
    note: 'D',
    line: 13,
    image: 'd4.png',
    position: '4³'
  }
];

let currentNoteNr = getRandomNoteNr();
let useRandomScales = false;
let currentScaleNr = 0; // getRandomScaleNr();

function getRandomNoteNr() {
  const max = notes.length;
  return Math.floor(max * Math.random());
}

function getRandomScaleNr() {
  const max = scales.length;
  return Math.floor(max * Math.random());
}

function noteToLine(note) {
  // No default case, this is the whole universe of notes
  // eslint-disable-next-line default-case
  switch (note) {
    case 'A':
      return 6;
    case 'B':
      return 5;
    case 'C':
      return 4;
    case 'D':
      return 3;
    case 'E':
      return 2;
    case 'F':
      return 1;
    case 'G':
      return 0;
  }
}

const NoteStore = assign({}, GenericStore, {
  setNewNoteNr: function() {
    const oldNoteNr = currentNoteNr;
    currentNoteNr = getRandomNoteNr();
    if (currentNoteNr === oldNoteNr) {
      // Avoid the same note twice in a row
      currentNoteNr = getRandomNoteNr();
    }
    if (useRandomScales) {
      currentScaleNr = getRandomScaleNr();
    }
    this.emitChange();
  },

  getNoteNr: function() {
    return currentNoteNr;
  },

  getNoteInfo: function(noteNr) {
    return notes[noteNr];
  },

  getNote: function(noteNr) {
    return notes[noteNr].note;
  },

  getScaleNr: function() {
    return currentScaleNr;
  },

  setScaleNr: function(scaleNr) {
    currentScaleNr = scaleNr;
    useRandomScales = false;
    NoteStore.emitChange();
  },

  useRandomScales: function() {
    return useRandomScales;
  },

  setUseRandomScales: function() {
    useRandomScales = true;
    currentScaleNr = getRandomScaleNr();
    NoteStore.emitChange();
  },

  getScaleInfo: function(scaleNr) {
    return scales[scaleNr];
  },

  getImage: function(noteNr) {
    return notes[noteNr].image;
  },

  getCurrentImage: function() {
    return notes[currentNoteNr].image;
  },

  isCurrentNote: function(note) {
    if (currentScaleNr) {
      // If the scale is not C Major, figure out the note suffix
      let baseNote = notes[currentNoteNr].note;
      const scale = scales[currentScaleNr];
      const line = noteToLine(baseNote);
      let hasLine = false
      if (scale.sharps) {
        // for the sharps, G=0, F=1, E=2, D=3, C=4, B=5, A=6
        scale.sharps.forEach((nr) => {if (nr === line) { hasLine = true;}})
        if (hasLine) {
          baseNote += '-sharp';
        }
      } else if (scale.flats) {
        // for the sharps, G=0, F=1, E=2, D=3, C=4, B=5, A=6
        scale.flats.forEach((nr) => {if (nr === line) { hasLine = true;}})
        if (hasLine) {
          baseNote += '-flat';
        }
      }
      return baseNote === note;
    }
    return (notes[currentNoteNr].note === note) || (notes[currentNoteNr].position === note);
  }

});

// for D major, F and C are sharps?
export default NoteStore;
